import * as THREE from "three";
import { config } from "../config.js";
import { mission } from "../sim/missions.js";

// ステージの見た目(2026-10-29 オーナー決定: 1回の中で移り変わる)。
// どのステージかは試合の時間(world.time)だけで決まるので、リプレイ・R.W.N.D.でも同じ景色になる。
// ここは描くだけで、試合の計算には関わらない。素材は SENSEOFVOID からコピーした画像(assets/CREDITS.md)。

export function stageIndexAt(time) {
  let i = 0;
  mission().stages.forEach((s, k) => {
    if (time >= s.from) i = k;
  });
  return i;
}

// 今のステージと、次へ移り変わる度合い(0〜1)
function stageMix(time) {
  const i = stageIndexAt(time);
  const t = Math.min(1, (time - mission().stages[i].from) / config.stageFadeSeconds);
  return { i, prev: Math.max(0, i - 1), t: i === 0 ? 1 : t };
}

const loader = new THREE.TextureLoader();
function tex(url, repeatX = 1, repeatY = 1) {
  const t = loader.load(url);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeatX, repeatY);
  t.anisotropy = 8;
  return t;
}

// 岩の形(球を少し凸凹にした形。見た目だけなので Math.random でよい)
export function rockGeometry() {
  const g = new THREE.IcosahedronGeometry(1, 2);
  const p = g.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < p.count; i++) {
    v.fromBufferAttribute(p, i);
    const n = 0.75 + 0.35 * Math.sin(v.x * 3.1 + v.y * 1.7) * Math.cos(v.z * 2.3) + Math.random() * 0.1;
    v.multiplyScalar(n);
    p.setXYZ(i, v.x, v.y, v.z);
  }
  g.computeVertexNormals();
  return g;
}

export function createStages(scene, { earth, moon, stars }) {
  const flow = () => config.field.scrollSpeed * 6; // 背景が流れる速さ(星と同じ)
  const groups = {};

  // --- アステロイド帯: 道の外側を流れていく岩 ---
  const rockCount = 160;
  const rocks = new THREE.InstancedMesh(rockGeometry(), new THREE.MeshStandardMaterial({ map: tex("assets/textures/asteroid-color.jpg"), roughness: 1, metalness: 0.05, transparent: true }), rockCount);
  rocks.frustumCulled = false;
  const rockData = [];
  for (let i = 0; i < rockCount; i++) rockData.push(resetRock({}, true));
  function resetRock(r, anywhere) {
    // 自機の動ける範囲(と少しの余白)を空けて置く
    const a = Math.random() * Math.PI * 2;
    const d = 110 + Math.random() * 500;
    r.x = Math.cos(a) * d;
    r.y = Math.sin(a) * d * 0.6;
    r.z = anywhere ? -2600 + Math.random() * 2600 : -2600 - Math.random() * 400;
    r.s = 6 + Math.random() * Math.random() * 60;
    r.rx = Math.random() * 6;
    r.ry = Math.random() * 6;
    r.spin = (Math.random() - 0.5) * 0.8;
    return r;
  }
  groups.asteroid = rocks;
  scene.add(rocks);

  // --- 廃コロニー: 中から見た円筒(陸の帯と窓の帯) ---
  const colony = new THREE.Group();
  const colonyR = 320;
  const colonyLen = 6000;
  const landTex = tex("assets/textures/colony-land.jpg", 3, 6);
  const windowTex = tex("assets/textures/colony-window.jpg", 3, 6);
  const metalTex = tex("assets/textures/colony-metal.jpg", 6, 12);
  // 円筒を6本の帯に分け、陸・窓・陸・窓…と貼る(SENSEOFVOID の廃コロニーと同じ並び)
  const colonyMats = [];
  for (let k = 0; k < 6; k++) {
    const g = new THREE.CylinderGeometry(colonyR, colonyR, colonyLen, 8, 1, true, (k / 6) * Math.PI * 2, Math.PI / 3);
    g.rotateX(Math.PI / 2);
    const mat = new THREE.MeshStandardMaterial({ map: k % 2 ? windowTex : landTex, side: THREE.BackSide, roughness: 0.95, transparent: true, emissive: new THREE.Color(k % 2 ? 0x223355 : 0x000000), emissiveIntensity: k % 2 ? 0.8 : 0 });
    colonyMats.push(mat);
    colony.add(new THREE.Mesh(g, mat));
  }
  // 骨組みの輪(流れていく)
  const ribs = [];
  const ribMat = new THREE.MeshStandardMaterial({ map: metalTex, roughness: 0.7, metalness: 0.6, transparent: true });
  colonyMats.push(ribMat);
  for (let i = 0; i < 10; i++) {
    const rib = new THREE.Mesh(new THREE.TorusGeometry(colonyR - 8, 7, 6, 48), ribMat);
    rib.position.z = -i * 300;
    colony.add(rib);
    ribs.push(rib);
  }
  colony.position.z = -colonyLen / 2 + 400;
  groups.colony = colony;
  scene.add(colony);

  // --- 月面: 下に広がる地面 ---
  const moonGround = new THREE.Mesh(new THREE.PlaneGeometry(6000, 6000, 1, 1), new THREE.MeshStandardMaterial({ map: tex("assets/textures/moon_2048.jpg", 4, 4), roughness: 1, transparent: true }));
  moonGround.rotation.x = -Math.PI / 2;
  moonGround.position.set(0, -140, -2600);
  groups.moon = moonGround;
  scene.add(moonGround);

  // --- 大気圏突入: 前から来る炎の筋 ---
  const fireCount = 260;
  const firePos = new Float32Array(fireCount * 6);
  const fireGeo = new THREE.BufferGeometry();
  fireGeo.setAttribute("position", new THREE.BufferAttribute(firePos, 3).setUsage(THREE.DynamicDrawUsage));
  const fire = new THREE.LineSegments(fireGeo, new THREE.LineBasicMaterial({ color: 0xffa060, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
  fire.frustumCulled = false;
  const fireData = [];
  for (let i = 0; i < fireCount; i++) fireData.push(resetFire({}, true));
  function resetFire(f, anywhere) {
    const a = Math.random() * Math.PI * 2;
    const d = 70 + Math.random() * 260;
    f.x = Math.cos(a) * d;
    f.y = Math.sin(a) * d * 0.7;
    f.z = anywhere ? -1500 + Math.random() * 1500 : -1500;
    f.len = 40 + Math.random() * 120;
    return f;
  }
  groups.atmosphere = fire;
  scene.add(fire);

  // ステージごとの背景の色・霧・地球と月の置き方
  const looks = {
    orbit: { bg: 0x02030a, fog: 0, earth: [-4600, -3000, -6500, 1], moon: [2600, 1500, -7000, 1], stars: 1 },
    asteroid: { bg: 0x05040a, fog: 0, earth: [-5200, -3600, -7500, 0.7], moon: [3000, 1800, -7600, 1], stars: 0.8 },
    colony: { bg: 0x0a0d14, fog: 0.00035, earth: [-5200, -3600, -7500, 0], moon: [3000, 1800, -7600, 0], stars: 0 },
    moon: { bg: 0x010205, fog: 0, earth: [-1800, 1900, -7000, 0.9], moon: [0, 0, -9000, 0], stars: 1 },
    atmosphere: { bg: 0x2a0d06, fog: 0.00045, earth: [0, -5200, -5600, 1.6], moon: [0, 0, -9000, 0], stars: 0.15 },
  };
  scene.fog = new THREE.FogExp2(0x000000, 0);
  const cA = new THREE.Color();
  const cB = new THREE.Color();
  const m4 = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const v = new THREE.Vector3();
  const sc = new THREE.Vector3();
  let propScale = 1;
  let lastStage = -1;

  function setOpacity(id, a) {
    const g = groups[id];
    g.visible = a > 0.01;
    const mats = id === "colony" ? colonyMats : [g.material];
    for (const m of mats) m.opacity = a;
  }

  // 毎フレーム。戻り値: ステージが変わった瞬間なら、そのステージ(画面の見出し用)
  function update(time, dtReal, timeScale) {
    const { i, prev, t } = stageMix(time);
    const cur = mission().stages[i].id;
    const old = mission().stages[prev].id;
    const weight = (id) => (id === cur ? t : id === old && i !== prev ? 1 - t : 0);
    for (const id of Object.keys(groups)) setOpacity(id, weight(id));

    const a = looks[old];
    const b = looks[cur];
    const k = i === prev ? 1 : t;
    scene.background.copy(cA.set(a.bg).lerp(cB.set(b.bg), k));
    scene.fog.color.copy(scene.background);
    scene.fog.density = a.fog + (b.fog - a.fog) * k;
    for (const [obj, key] of [[earth, "earth"], [moon, "moon"]]) {
      const pa = a[key];
      const pb = b[key];
      obj.position.set(pa[0] + (pb[0] - pa[0]) * k, pa[1] + (pb[1] - pa[1]) * k, pa[2] + (pb[2] - pa[2]) * k);
      const s = pa[3] + (pb[3] - pa[3]) * k;
      obj.scale.setScalar(Math.max(0.001, s));
      obj.visible = s > 0.01;
    }
    stars.material.opacity = 0.7 * (a.stars + (b.stars - a.stars) * k);

    const d = flow() * dtReal * timeScale;
    if (rocks.visible) {
      const n = Math.round(rockCount * propScale);
      for (let r = 0; r < n; r++) {
        const o = rockData[r];
        o.z += d * 0.7;
        if (o.z > 120) resetRock(o, false);
        o.rx += o.spin * dtReal * timeScale;
        q.setFromEuler(e.set(o.rx, o.ry + o.rx * 0.5, 0));
        m4.compose(v.set(o.x, o.y, o.z), q, sc.setScalar(o.s));
        rocks.setMatrixAt(r, m4);
      }
      rocks.count = n;
      rocks.instanceMatrix.needsUpdate = true;
    }
    if (colony.visible) {
      // 円筒は長いので動かさず、画像を流して進んで見せる。輪は実際に流す
      const step = d / colonyLen;
      landTex.offset.y += step * 6;
      windowTex.offset.y += step * 6;
      for (const rib of ribs) {
        rib.position.z += d;
        if (rib.position.z > colonyLen / 2 + 200) rib.position.z -= 3000;
      }
      colony.rotation.z += dtReal * 0.02 * timeScale;
    }
    if (moonGround.visible) moonGround.material.map.offset.y += (d / 6000) * 4;
    if (fire.visible) {
      for (let f = 0; f < fireCount; f++) {
        const o = fireData[f];
        o.z += d * 1.4;
        if (o.z > 60) resetFire(o, false);
        firePos.set([o.x, o.y, o.z, o.x, o.y, o.z - o.len], f * 6);
      }
      fireGeo.setDrawRange(0, Math.round(fireCount * propScale) * 2);
      fireGeo.attributes.position.needsUpdate = true;
    }

    if (i !== lastStage) {
      const first = lastStage === -1;
      lastStage = i;
      if (!first || i > 0) return mission().stages[i];
    }
    return null;
  }

  return {
    update,
    setPropScale(s) {
      propScale = s;
    },
    reset() {
      lastStage = -1;
    },
  };
}
